import { useTitle } from "@/hooks/use-title.hook.ts";
import { HelpCollectionCards } from "@/pages/help/components/HelpCollectionCards.tsx";
import { HelpSearchResults } from "@/pages/help/components/HelpSearchResults.tsx";
import { useHelp } from "@/pages/help/help.hook.tsx";
import { useHelpSearch } from "@/pages/help/use-help-search.hook.tsx";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { resolveHelpLanguage } from "@/utils/help-language.ts";
import { Box, Page, Search, Skeleton, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export const Help = () => {
  const { t, i18n } = useTranslation();
  const language = resolveHelpLanguage(i18n.resolvedLanguage || i18n.language);

  useTitle(t("common:help"));

  const { helpIndex, findCategoryBySlug } = useHelp(language);

  const currentCategory = useMemo(() => {
    if (!helpIndex) {
      return null;
    }
    return findCategoryBySlug("");
  }, [findCategoryBySlug, helpIndex]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.help.viewed);
  });

  const { query, setQuery, clearQuery, results, hasQuery, isSearching } = useHelpSearch({
    searchIndex: helpIndex?.searchIndex || null,
  });

  return (
    <>
      <Page>
        <Page.Header title={t("common:help")}></Page.Header>
        <Page.Content>
          <Stack className={"py-10 px-16 gap-10"}>
            <Title
              header={t("common:help_center")}
              caption={currentCategory?.description}
              size={"lg"}
              paddingTop={true}
              paddingBottom={true}
            />

            <Box>
              <Search
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClear={clearQuery}
                placeholder={t("core:help_how_can_i_help_you")}
                clearable={true}
                borderless={false}
              />
            </Box>

            {hasQuery ? (
              <HelpSearchResults
                results={results}
                query={query}
                isSearching={isSearching}
                articles={helpIndex?.articles || []}
              />
            ) : (
              <>
                {!currentCategory && <Skeleton className={"w-full h-10"} />}
                {currentCategory && <HelpCollectionCards collections={currentCategory.children} />}
              </>
            )}
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
};
